import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Flex,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  Spinner,
  Center,
  Input,
  IconButton,
  Avatar,
} from "@chakra-ui/react";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import axios from "axios";
import proxy from "../../proxy";

const LIST_POLL_MS = 6000;
const THREAD_POLL_MS = 4000;

const formatTime = (s) =>
  new Date((s || "").replace(" ", "T")).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

const formatDate = (s) =>
  new Date((s || "").replace(" ", "T")).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

const AdminChat = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(true);

  const scrollRef = useRef(null);
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;

  const scrollToBottom = () =>
    requestAnimationFrame(() => {
      if (scrollRef.current)
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(
        `${proxy}server_mts/admin/chat/Conversations.php`,
        { headers: { "Cache-Control": "no-cache" } }
      );
      setConversations(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      /* ignora */
    } finally {
      setLoadingList(false);
    }
  }, []);

  const fetchThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await axios.get(`${proxy}server_mts/admin/chat/Thread.php`, {
        params: { id },
        headers: { "Cache-Control": "no-cache" },
      });
      if (activeIdRef.current !== id) return; // s-a schimbat intre timp
      setActiveConv(res.data?.conversation || null);
      setMessages(res.data?.messages || []);
      scrollToBottom();
    } catch (e) {
      /* ignora */
    }
  }, []);

  // polling lista
  useEffect(() => {
    fetchConversations();
    const t = setInterval(fetchConversations, LIST_POLL_MS);
    return () => clearInterval(t);
  }, [fetchConversations]);

  // polling thread activ
  useEffect(() => {
    if (!activeId) return;
    fetchThread(activeId);
    const t = setInterval(() => fetchThread(activeId), THREAD_POLL_MS);
    return () => clearInterval(t);
  }, [activeId, fetchThread]);

  const openConversation = (id) => {
    setActiveId(id);
    setMessages([]);
    // marcheaza local ca citit
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, unread: 0 } : c))
    );
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    const optimistic = {
      id: `tmp_${Date.now()}`,
      sender: "admin",
      body,
      created_at: new Date().toISOString().slice(0, 19).replace("T", " "),
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    scrollToBottom();
    try {
      await axios.post(`${proxy}server_mts/admin/chat/Reply.php`, {
        conversation_id: activeId,
        body,
      });
      fetchThread(activeId);
      fetchConversations();
    } catch (e) {
      /* ignora — ramane optimist */
    } finally {
      setSending(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.900">
      {/* Header */}
      <Box
        bg="linear-gradient(135deg, rgba(26,32,44,0.98), rgba(45,55,72,0.95))"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        py={4}
        px={6}
      >
        <Container maxW="1400px">
          <HStack spacing={4}>
            <IconButton
              icon={<ArrowBackIcon />}
              variant="ghost"
              colorScheme="teal"
              onClick={() => navigate("/admin/dashboard")}
              aria-label="Inapoi"
            />
            <VStack align="start" spacing={0}>
              <Heading
                size="md"
                bgGradient="linear(to-r, teal.300, cyan.400)"
                bgClip="text"
              >
                💬 Mesaje
              </Heading>
              <Text fontSize="xs" color="gray.500">
                Conversații cu vizitatorii site-ului
              </Text>
            </VStack>
          </HStack>
        </Container>
      </Box>

      <Container maxW="1400px" py={6}>
        <Flex
          h={{ base: "auto", md: "70vh" }}
          gap={4}
          direction={{ base: "column", md: "row" }}
        >
          {/* Lista conversatii */}
          <Box
            w={{ base: "100%", md: "320px" }}
            bg="gray.800"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.700"
            overflowY="auto"
            maxH={{ base: "300px", md: "100%" }}
          >
            {loadingList ? (
              <Center py={10}>
                <Spinner color="teal.400" />
              </Center>
            ) : conversations.length === 0 ? (
              <Center py={10} px={4}>
                <Text color="gray.500" fontSize="sm" textAlign="center">
                  Nicio conversație încă.
                </Text>
              </Center>
            ) : (
              <VStack align="stretch" spacing={0}>
                {conversations.map((c) => (
                  <HStack
                    key={c.id}
                    px={3}
                    py={3}
                    spacing={3}
                    cursor="pointer"
                    bg={activeId === c.id ? "whiteAlpha.100" : "transparent"}
                    _hover={{ bg: "whiteAlpha.50" }}
                    borderBottom="1px solid"
                    borderColor="whiteAlpha.100"
                    onClick={() => openConversation(c.id)}
                  >
                    <Avatar
                      size="sm"
                      name={c.visitor_name || "?"}
                      bg="teal.600"
                    />
                    <Box flex="1" minW={0}>
                      <HStack justify="space-between">
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color="gray.200"
                          noOfLines={1}
                        >
                          {c.visitor_name || "Vizitator anonim"}
                        </Text>
                        <Text fontSize="2xs" color="gray.500">
                          {formatDate(c.last_activity)}
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                          {c.last_sender === "admin" ? "Tu: " : ""}
                          {c.last_message || ""}
                        </Text>
                        {c.unread > 0 && (
                          <Badge
                            bg="red.500"
                            color="white"
                            borderRadius="full"
                            fontSize="2xs"
                          >
                            {c.unread}
                          </Badge>
                        )}
                      </HStack>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            )}
          </Box>

          {/* Thread */}
          <Flex
            flex="1"
            direction="column"
            bg="gray.800"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.700"
            overflow="hidden"
            minH={{ base: "400px", md: "auto" }}
          >
            {!activeId ? (
              <Center flex="1">
                <Text color="gray.500">
                  Selectează o conversație din stânga.
                </Text>
              </Center>
            ) : (
              <>
                <HStack
                  px={4}
                  py={3}
                  borderBottom="1px solid"
                  borderColor="whiteAlpha.200"
                  spacing={3}
                >
                  <Avatar
                    size="sm"
                    name={activeConv?.visitor_name || "?"}
                    bg="teal.600"
                  />
                  <Text fontWeight="semibold" color="gray.200">
                    {activeConv?.visitor_name || "Vizitator anonim"}
                  </Text>
                </HStack>

                <VStack
                  ref={scrollRef}
                  flex="1"
                  align="stretch"
                  spacing={2}
                  px={4}
                  py={3}
                  overflowY="auto"
                >
                  {messages.map((m) => {
                    const isAdmin = m.sender === "admin";
                    return (
                      <Flex
                        key={m.id}
                        justify={isAdmin ? "flex-end" : "flex-start"}
                      >
                        <Box
                          maxW="70%"
                          px={3}
                          py={2}
                          borderRadius="lg"
                          bg={isAdmin ? "teal.500" : "gray.700"}
                          color="white"
                        >
                          <Text fontSize="sm" whiteSpace="pre-wrap">
                            {m.body}
                          </Text>
                          <Text
                            fontSize="2xs"
                            color="whiteAlpha.700"
                            textAlign="right"
                            mt={0.5}
                          >
                            {formatTime(m.created_at)}
                          </Text>
                        </Box>
                      </Flex>
                    );
                  })}
                </VStack>

                <HStack
                  p={3}
                  borderTop="1px solid"
                  borderColor="whiteAlpha.200"
                >
                  <Input
                    placeholder="Scrie un răspuns…"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && !e.shiftKey
                        ? (e.preventDefault(), send())
                        : null
                    }
                    bg="gray.900"
                    color="white"
                    borderColor="whiteAlpha.300"
                  />
                  <IconButton
                    icon={<ArrowForwardIcon />}
                    colorScheme="teal"
                    aria-label="Trimite"
                    onClick={send}
                    isLoading={sending}
                    isDisabled={!draft.trim()}
                  />
                </HStack>
              </>
            )}
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
};

export default AdminChat;
